import type { TmInfoEntry } from './TmInfoModal';
import { TM_EX as X, fmtAud } from './timeAndMaterialsExamples';

/** apply-tooltip-pattern — Timesheets table column dictionary */
export const TS_COL_INFO: Record<string, TmInfoEntry> = {
  ts_copy: {
    t: 'Copy',
    ctx: 'Timesheets · column',
    d: 'Tick rows you want to push to Billable items as Service lines. Only copied rows become invoice lines — unchecked timesheets stay labour cost only.',
    e: `Tick ${X.techLead} · ${X.srCode} (${X.srBillHrs.toFixed(2)} billable hrs on ${X.jobRef} ${X.jobTitle}) → Copy to billable (1) creates a Service line at ${fmtAud(X.srServiceHr)}/hr.`,
    f: 'Copied lines = rows WHERE Copy = ✓ → Billable items (Type = Service)',
    s: 'Timesheet row checkbox on this job workspace.',
  },
  ts_staff: {
    t: 'Staff',
    ctx: 'Timesheets · column',
    d: 'The technician who worked the shift. Used to group labour cost and to label Service lines when copied to billable.',
    e: `${X.techLead} — lead tech on ${X.jobRef} at ${X.customer}, ${X.site}.`,
    f: 'Staff ← Timesheet.staff',
    s: 'Scheduling → Timesheet entry for this job visit.',
  },
  ts_service: {
    t: 'Service code',
    ctx: 'Timesheets · column',
    d: 'The service item this labour maps to — drives cost rate (pay) and service rate (sell).',
    e: `${X.srCode} (${X.srLabel}) → cost ${fmtAud(X.srCostHr)}/hr, service ${fmtAud(X.srServiceHr)}/hr from Products & Services price book.`,
    f: 'Service code → Service item → Cost rate + Service rate',
    s: 'Products & Services → Service item master (e.g. HVAC-SR, HVAC-APP, REF-GAS).',
  },
  ts_date: {
    t: 'Date',
    ctx: 'Timesheets · column',
    d: 'Calendar date the work was performed — for audit and invoice period matching.',
    e: `${X.visitDate} — Daikin VRV service visit on ${X.jobRef}.`,
    f: 'Date ← Timesheet.workDate',
    s: 'Scheduling → Timesheet.workDate.',
  },
  ts_start: {
    t: 'Start',
    ctx: 'Timesheets · column',
    d: 'Clock-on time for this segment. With End, derives timesheet total hours when hours are not typed directly.',
    e: `9:00 AM clock-on — ${X.techLead} starts indoor FCU inspection at ${X.customer}.`,
    f: 'Start ← Timesheet.segmentStart',
    s: 'Time & attendance → clock-on (or manual entry on row).',
  },
  ts_end: {
    t: 'End',
    ctx: 'Timesheets · column',
    d: 'Clock-off time for this segment.',
    e: `1:00 PM clock-off → ${X.srTotalHrs.toFixed(2)} timesheet total hours on the ${X.srCode} row.`,
    f: 'End ← Timesheet.segmentEnd',
    s: 'Time & attendance → clock-off (or manual entry on row).',
  },
  ts_totalhrs: {
    t: 'Timesheet total hours',
    ctx: 'Timesheets · column',
    d: 'All hours worked on this row — billable and non-billable. Used for COST only (× service item cost rate). Never use billable hours for cost.',
    e: `${X.techLead} ${X.srCode}: ${X.srTotalHrs.toFixed(2)} timesheet total hours × ${fmtAud(X.srCostHr)} = ${fmtAud(X.srTotalHrs * X.srCostHr)} cost. ${X.techAssist} ${X.appCode}: ${X.appTotalHrs.toFixed(2)} total (cost uses all ${X.appTotalHrs.toFixed(2)} hrs, not ${X.appBillHrs.toFixed(2)} billable).`,
    f: 'Row cost = Timesheet total hours × Service item cost rate\n\n⚠ Cost side — NOT billable hours',
    s: 'Timesheet.totalHours on this row.',
  },
  ts_billhrs: {
    t: 'Timesheet billable hours',
    ctx: 'Timesheets · column',
    d: 'Hours charged to the customer. Can be less than timesheet total hours (travel, switchroom standby). Used for SALE only (× service rate).',
    e: `${X.techAssist} ${X.appCode}: ${X.appBillHrs.toFixed(2)} billable of ${X.appTotalHrs.toFixed(2)} timesheet total hours — plantroom travel not billed → ${X.appBillHrs.toFixed(2)} × ${fmtAud(X.appServiceHr)} = ${fmtAud(X.appBillHrs * X.appServiceHr)} sales amount.`,
    f: 'Row sale = Timesheet billable hours × Service rate\n\n⚠ Sales side — NOT total hours',
    s: 'Timesheet.billableHours on this row.',
  },
  ts_costrate: {
    t: 'Cost rate',
    ctx: 'Timesheets · column',
    d: 'What you pay per hour for this service code — internal cost rate, not the customer service rate.',
    e: `${X.srCode} ${fmtAud(X.srCostHr)}/hr on ${X.techLead}'s row; ${X.appCode} ${fmtAud(X.appCostHr)}/hr on ${X.techAssist}'s assist row.`,
    f: 'Cost rate ← Service item.costRate (ex GST, per hour)',
    s: 'Products & Services → Service item → Cost rate.',
  },
  ts_cost: {
    t: 'Cost',
    ctx: 'Timesheets · column',
    d: 'Labour cost for this row — timesheet total hours × cost rate. Counts in Job P&L even if the row is not copied to billable.',
    e: `${X.srTotalHrs.toFixed(2)} timesheet total hours × ${fmtAud(X.srCostHr)} = ${fmtAud(X.srTotalHrs * X.srCostHr)} on ${X.techLead} · ${X.srCode} · ${X.jobTitle}.`,
    f: 'Cost = Timesheet total hours × Service item cost rate',
    s: 'Derived: Timesheet.totalHours × Service item.costRate (all hours, not billable).',
  },
  ts_servicerate: {
    t: 'Service rate',
    ctx: 'Timesheets · column',
    d: 'Customer price per hour for this service code — unit sell before multiplying by billable hours.',
    e: `${X.srCode} service rate ${fmtAud(X.srServiceHr)}/hr → ${X.srBillHrs.toFixed(2)} billable hrs = ${fmtAud(X.srBillHrs * X.srServiceHr)} sales amount on ${X.jobRef}.`,
    f: 'Service rate ← Service item.salesRate (ex GST, per hour)',
    s: 'Products & Services → Service item → Sales rate.',
  },
  ts_sale: {
    t: 'Sale',
    ctx: 'Timesheets · column',
    d: 'Sales amount for this row — timesheet billable hours × service rate. Rolls into Timesheets Sale cascade; becomes billable when copied.',
    e: `${X.srBillHrs.toFixed(2)} billable hrs × ${fmtAud(X.srServiceHr)} ${X.srCode} = ${fmtAud(X.srBillHrs * X.srServiceHr)} sale on ${X.techLead}'s row.`,
    f: 'Sale = Timesheet billable hours × Service rate',
    s: 'Derived: Timesheet.billableHours × Service item.salesRate.',
  },
};

/** apply-tooltip-pattern — Billable items table column dictionary */
export const BI_COL_INFO: Record<string, TmInfoEntry> = {
  bi_bill: {
    t: 'Bill',
    ctx: 'Billable items · column',
    d: 'Include this line on the customer invoice. Unticked lines stay on the job for costing but contribute $0 to Amount.',
    e: `Tick ${X.thrmCode} (${X.thrmDesc}) → ${fmtAud(X.thrmSell)} on invoice. Untick HVAC-CONSUM (nitrile gloves, zip ties) goodwill line → $0 on invoice.`,
    f: 'Invoice line created IF Bill = ✓ WHEN + Sales Document',
    s: 'Billable line.billable flag on this row.',
  },
  bi_type: {
    t: 'Type',
    ctx: 'Billable items · column',
    d: 'Service (labour from timesheets), Product (van/warehouse stock), or Expense (call-out, fees).',
    e: `Service · ${X.srCode} copied from timesheets. Product · ${X.thrmCode} drawn from van. Expense · ${X.calloutCode} after-hours fee.`,
    f: 'Type ∈ {Service, Product, Expense}',
    s: 'Billable line.lineType — set on copy from timesheets or when added manually.',
  },
  bi_code: {
    t: 'Code',
    ctx: 'Billable items · column',
    d: 'Inventory or service code on the invoice.',
    e: `${X.srCode} labour; ${X.thrmCode} thermistor; ${X.gasCode} refrigerant cylinder on ${X.jobRef}.`,
    f: 'Code ← Service item.code OR Product.code',
    s: 'Products & Services master, or Timesheet.serviceCode when copied.',
  },
  bi_desc: {
    t: 'Description',
    ctx: 'Billable items · column',
    d: 'Customer-facing line text on quote or invoice.',
    e: `"${X.techLead} — Daikin VRV annual service labour" on copied ${X.srCode} line. "${X.thrmDesc}" on product line.`,
    f: 'Description ← editable billable line text',
    s: 'Auto-filled on timesheet copy (staff + work type); editable per row.',
  },
  bi_reqqty: {
    t: 'Required Qty',
    ctx: 'Billable items · column',
    d: 'Planned quantity from quote or scope. Does not drive totals — Actual Qty does.',
    e: `Quote on ${X.jobRef} allowed 1 × ${X.thrmCode}; required qty 1.00 before tech found failed thermistor on second FCU.`,
    f: 'Planning only — not in Cost total or Amount',
    s: 'Billable line.requiredQty.',
  },
  bi_actualqty: {
    t: 'Actual Qty',
    ctx: 'Billable items · column',
    d: 'Quantity actually used or delivered. On Product, Expense and PO lines it drives BOTH Cost total and Amount. On Service lines copied from timesheets it is the BILLABLE hours — it drives Amount only; labour cost keeps using timesheet total hours.',
    e: `${X.techAssist} works ${X.appTotalHrs.toFixed(2)} hrs but bills ${X.appBillHrs.toFixed(2)} → Service line Actual Qty = ${X.appBillHrs.toFixed(2)} → Amount ${fmtAud(X.appBillHrs * X.appServiceHr)}. Cost stays ${fmtAud(X.appTotalHrs * X.appCostHr)} in Timesheets (${X.appTotalHrs.toFixed(2)} × ${fmtAud(X.appCostHr)}). Product: 1.00 × ${X.thrmCode} @ ${fmtAud(X.thrmSell)}.`,
    f: 'Product / Expense / PO:\n  Cost total = Actual qty × Cost (Ex)\n  Amount     = Actual qty × Sales (Ex)\n\nService (from timesheet):\n  Amount     = Actual qty (billable hrs) × Sales (Ex)\n  Cost total = timesheet total hrs × cost rate — NOT this qty',
    s: 'Billable line.actualQty — timesheet billable hours when copied, else edited here.',
  },
  bi_costex: {
    t: 'Cost (Ex)',
    ctx: 'Billable items · column',
    d: 'Unit purchase cost ex GST. Service lines copied from timesheets show "See timesheets" — labour cost is read from the Timesheets section (total hours × cost rate) and is never entered here. Products use van draw-down cost. PO lines use the whole PO ex-GST as one unit.',
    e: `Product ${X.thrmCode}: ${fmtAud(X.thrmCost)} unit cost from van stock (${X.thrmDesc}). Service ${X.srCode}: See timesheets — ${fmtAud(X.srTotalHrs * X.srCostHr)} on ${X.techLead}'s row (${X.srTotalHrs.toFixed(2)} hrs × ${fmtAud(X.srCostHr)}). PO ${X.poId}: ${fmtAud(X.poExGst)} ex-GST from ${X.poSupplier} (${X.poDesc}).`,
    f: 'By line type:\n  Service (from timesheet) → See timesheets:\n    timesheet total hrs × cost rate\n  Product → Inventory purchase price\n  PO row → PO.exGst (one line per PO)',
    s: 'Product: Inventory price book → purchase price per code.\nService: $0 on billable row — Timesheet.totalHours × Service item.costRate.\nPO: Purchase Orders → PO.exGst for PO id (e.g. ' + X.poId + ').',
  },
  bi_salesex: {
    t: 'Sales (Ex)',
    ctx: 'Billable items · column',
    d: 'Unit sell price ex GST — service rate for labour, shelf price for products, fixed fee for expenses.',
    e: `${X.srCode}: ${fmtAud(X.srServiceHr)}/hr service rate. ${X.thrmCode}: ${fmtAud(X.thrmSell)}. ${X.calloutCode}: ${fmtAud(X.calloutSell)} fixed fee. PO recharge: ${fmtAud(X.poExGst)} × (1 + ${X.poMarkupPct}%) markup.`,
    f: 'Unit sales price (ex GST) per hour, unit, or PO recharge',
    s: 'Service: Service item.salesRate.\nProduct/Expense: Inventory sales price.\nPO: PO.exGst × (1 + Supplier.defaultMarkup%).',
  },
  bi_costtotal: {
    t: 'Cost total',
    ctx: 'Billable items · column',
    d: 'Your total cost on this line, read-only. Product/Expense/PO: actual qty × unit cost. Service lines copied from timesheets mirror the timesheet cost — total hours × cost rate — so you may bill 4 hrs while the cost reflects all 8 worked.',
    e: `1.00 × ${fmtAud(X.thrmCost)} = ${fmtAud(X.thrmCost)} on ${X.thrmCode} replaced on FCU-42. ${X.appCode}: mirrors ${fmtAud(X.appTotalHrs * X.appCostHr)} from ${X.techAssist}'s timesheet (${X.appTotalHrs.toFixed(2)} worked hrs × ${fmtAud(X.appCostHr)}) even though only ${X.appBillHrs.toFixed(2)} hrs are billed. PO line = ${fmtAud(X.poExGst)} (${X.poId}).`,
    f: 'Product / Expense / PO:\n  Cost total = Actual qty × Cost (Ex)\n\nService (from timesheet):\n  Cost total = timesheet total hrs × cost rate',
    s: 'Derived on row. Service: mirrors Timesheets — counted once in Job P&L, under Labour. PO lines: equals PO.exGst from Purchase Orders.',
  },
  bi_amount: {
    t: 'Amount',
    ctx: 'Billable items · column',
    d: 'Customer sales amount — actual qty × sales (ex). On Service lines that is billable hours × service rate; labour cost is never derived from this quantity. $0 when Bill is unticked.',
    e: `${X.srBillHrs.toFixed(2)} × ${fmtAud(X.srServiceHr)} = ${fmtAud(X.srBillHrs * X.srServiceHr)} ${X.srCode} labour. 1.00 × ${fmtAud(X.thrmSell)} = ${fmtAud(X.thrmSell)} ${X.thrmCode}. ${X.poId} recharge ${fmtAud(X.poExGst)} + ${X.poMarkupPct}% → ${fmtAud(Math.round(X.poExGst * (1 + X.poMarkupPct / 100) * 100) / 100)} on ${X.jobRef}.`,
    f: 'Amount = Actual qty × Sales (Ex)  IF Bill = ✓\n       = $0                    IF Bill off',
    s: 'Derived on row → Billable items Sale cascade → invoice on + Sales Document.',
  },
};
